import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
import { updateOrderAddress } from "../../actions/product";

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const UpdateOrderAddressModal = ({ show, onHide, order, onSuccess }) => {
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [deliveryContact, setDeliveryContact] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (order) {
      setDeliveryAddress(order.deliveryAddress || order.parent?.address || "");
      setCity(order.city || order.parent?.city || "Hyderabad");
      setState(order.state || order.parent?.state || "Telangana");
      setPincode(order.pincode || order.parent?.pincode || "");
      setDeliveryContact(order.deliveryContact || order.parent?.phoneNumber || "");
      setError(null);
    }
  }, [order]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!deliveryAddress.trim()) {
      setError("Please enter the delivery address.");
      return;
    }
    if (!city.trim()) {
      setError("Please enter the city.");
      return;
    }
    if (!state.trim()) {
      setError("Please enter the state.");
      return;
    }
    if (!pincode.trim()) {
      setError("Please enter the pincode.");
      return;
    }
    if (!/^\d{6}$/.test(pincode.trim())) {
      setError("Pincode must be exactly 6 digits.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = {
        deliveryAddress: deliveryAddress.trim(),
        city: city.trim(),
        state: state.trim(),
        pincode: pincode.trim(),
        deliveryContact: deliveryContact.trim(),
      };

      const result = await updateOrderAddress(order.id, payload);
      if (result && !result.error) {
        if (onSuccess) {
          onSuccess(result.order || { ...order, ...payload, isAddressEdited: true });
        }
        onHide();
      }
    } catch (err) {
      console.error("Error updating order address:", err);
      setError(err?.response?.data?.message || err.message || "Failed to update address.");
    } finally {
      setSaving(false);
    }
  };

  if (!order) return null;

  return (
    <Modal show={show} onHide={onHide} backdrop="static" centered size="lg">
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-5 fw-bold text-dark d-flex align-items-center">
          <i className="bi bi-geo-alt-fill text-danger me-2"></i>
          Update Order Address
          <span className="badge bg-secondary bg-opacity-10 text-dark border ms-2 fs-6 fw-normal font-monospace">
            {order.orderId || order.id}
          </span>
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="p-4">
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="py-2 small">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
            </Alert>
          )}

          {order.trackingId && order.status !== "CANCELLED" && order.status !== "DTDC_CANCELLED" && (
            <Alert variant="warning" className="py-2 small mb-3">
              <i className="bi bi-info-circle-fill me-2"></i>
              <strong>Notice:</strong> This order has an active DTDC consignment (<strong>{order.trackingId}</strong>).
              If the shipment has not yet been dispatched and you need courier routing to change, please cancel the DTDC consignment first, update the address here, and re-generate DTDC.
            </Alert>
          )}

          <div className="bg-light p-3 rounded mb-3 border">
            <Row className="small">
              <Col sm={6}>
                <span className="text-muted d-block">Parent / Customer:</span>
                <span className="fw-bold">{order.parent?.parentName || "N/A"}</span>
              </Col>
              <Col sm={6}>
                <span className="text-muted d-block">Current Shipping Method:</span>
                <span className="fw-semibold text-capitalize">{order.shippingMethod || "Home"}</span>
                {order.isAddressEdited && (
                  <span className="badge bg-info bg-opacity-25 text-primary border ms-2">Previously Edited</span>
                )}
              </Col>
            </Row>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold">
              <i className="bi bi-house-door me-1 text-primary"></i>Delivery Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter complete street address, flat/house number, building, landmark..."
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              required
            />
            <Form.Text className="text-muted small">
              Specify full street address without repeating city, state, or pincode.
            </Form.Text>
          </Form.Group>

          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-bold">
                  <i className="bi bi-building me-1 text-primary"></i>City <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. Hyderabad"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-bold">
                  <i className="bi bi-map me-1 text-primary"></i>State <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  list="indian-states-list"
                  type="text"
                  placeholder="e.g. Telangana"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  required
                />
                <datalist id="indian-states-list">
                  {INDIAN_STATES.map((s) => (
                    <option key={s} value={s} />
                  ))}
                </datalist>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label className="small fw-bold">
                  <i className="bi bi-mailbox me-1 text-primary"></i>Pincode <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="6-digit pincode"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-2">
            <Form.Label className="small fw-bold">
              <i className="bi bi-telephone me-1 text-primary"></i>Delivery Contact Phone
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. 9876543210"
              maxLength={15}
              value={deliveryContact}
              onChange={(e) => setDeliveryContact(e.target.value)}
            />
            <Form.Text className="text-muted small">
              Contact number used for delivery courier communication.
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check2-circle me-1"></i>Save Address
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateOrderAddressModal;
